/**
 * Panel de administración de Catálogos oficiales CNBV (RIPS F36).
 * Vive dentro de la pantalla de super_admin `ConfiguracionNormativa.jsx`.
 *
 * Funcionalidad:
 *   - Sube el archivo Excel oficial (multipart) y muestra el resumen de la carga.
 *   - Muestra el estado actual: total por catálogo + fecha de última carga.
 *   - Búsqueda por catálogo con texto (insensible a mayúsculas y acentos).
 *
 * Sin efectos colaterales: no toca clientes, reportes ni pld_config.
 */
import { useEffect, useRef, useState } from "react";
import { Upload, RefreshCw, Search, Database, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { api } from "@/lib/api";
import EquivalenciasActividadPanel from "@/components/pld/EquivalenciasActividadPanel";

const CATALOGOS = [
  { key: "ACTIVIDAD_ECONOMICA", label: "Actividad económica", esperado: 1263 },
  { key: "LOCALIDAD",           label: "Localidad",            esperado: 3689 },
  { key: "MONEDA",              label: "Moneda",               esperado: 190  },
  { key: "INSTRUMENTO_MONETARIO", label: "Instrumento monetario", esperado: 10 },
  { key: "TIPO_OPERACION_SOFOM", label: "Tipo de operación (SOFOM)", esperado: 19 },
  { key: "TIPO_REPORTE",        label: "Tipo de reporte",      esperado: 3    },
];

export default function CatalogosRipsPanel() {
  const [estado, setEstado] = useState({});
  const [cargando, setCargando] = useState(false);
  const [ultimoResumen, setUltimoResumen] = useState(null);
  const [busquedaCat, setBusquedaCat] = useState("TIPO_OPERACION_SOFOM");
  const [busquedaTexto, setBusquedaTexto] = useState("");
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const inputRef = useRef(null);

  const cargarEstado = async () => {
    try {
      const { data } = await api.get("/catalogos-rips/estado");
      setEstado(data.catalogos || {});
    } catch (err) {
      // Rol distinto de super_admin: estado no expuesto (403). No hacer ruido.
      if (err?.response?.status !== 403) {
        toast.error("No se pudo consultar el estado de los catálogos.");
      }
    }
  };

  useEffect(() => { cargarEstado(); }, []);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.xlsx$/i.test(file.name)) {
      toast.error("El archivo debe ser .xlsx");
      return;
    }
    setCargando(true);
    setUltimoResumen(null);
    try {
      const form = new FormData();
      form.append("archivo", file);
      const { data } = await api.post("/catalogos-rips/cargar", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUltimoResumen(data);
      toast.success(`Catálogos cargados · ${Object.keys(data.resumen || {}).length} hojas procesadas`);
      await cargarEstado();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al cargar el archivo.");
    } finally {
      setCargando(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleBuscar = async () => {
    setBuscando(true);
    try {
      const { data } = await api.get("/catalogos-rips/buscar", {
        params: { catalogo: busquedaCat, q: busquedaTexto },
      });
      setResultados(data.resultados || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error en la búsqueda.");
      setResultados([]);
    } finally {
      setBuscando(false);
    }
  };

  const fmtFecha = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" }); }
    catch { return iso; }
  };

  return (
    <div className="space-y-6" data-testid="catalogos-rips-panel">
      {/* Encabezado + carga */}
      <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-heading text-base font-semibold text-slate-800">
              Catálogos oficiales CNBV (RIPS F36)
            </h3>
            <p className="mt-1 max-w-2xl text-xs text-slate-500">
              Estos catálogos son normativa oficial idéntica para todas las entidades. Cargar el archivo Excel
              distribuido por la CNBV reemplaza por completo cada catálogo procesado. La hoja
              <code className="mx-1 rounded bg-slate-100 px-1 py-0.5 text-[10px]">TIPO_OPERACION_ASESORES_INV</code>
              se ignora automáticamente porque el proyecto es solo SOFOM.
            </p>
          </div>
          <div className="shrink-0">
            <input ref={inputRef} type="file" accept=".xlsx" hidden onChange={handleFile}
                   data-testid="catalogos-rips-file-input" />
            <Button data-testid="catalogos-rips-upload-btn" onClick={() => inputRef.current?.click()}
                    disabled={cargando} className="gap-2">
              {cargando ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {cargando ? "Procesando…" : "Cargar Excel oficial"}
            </Button>
          </div>
        </div>

        {ultimoResumen && (
          <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm"
               data-testid="catalogos-rips-resumen">
            <div className="flex items-center gap-2 font-medium text-emerald-800">
              <CheckCircle2 className="h-4 w-4" />
              Carga completada · {fmtFecha(ultimoResumen.cargado_en)}
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-slate-700 md:grid-cols-3">
              {Object.entries(ultimoResumen.resumen || {}).map(([cat, n]) => (
                <div key={cat} className="rounded bg-white px-2 py-1"
                     data-testid={`catalogos-rips-resumen-${cat}`}>
                  <span className="font-mono text-slate-500">{cat}</span>
                  <span className="ml-2 font-semibold text-emerald-700">{n}</span>
                </div>
              ))}
            </div>
            {(ultimoResumen.hojas_ignoradas || []).length > 0 && (
              <div className="mt-2 flex items-center gap-2 text-xs text-amber-800">
                <AlertTriangle className="h-3.5 w-3.5" />
                Hojas ignoradas (fuera de alcance): {ultimoResumen.hojas_ignoradas.join(", ")}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Estado por catálogo */}
      <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-3 flex items-center gap-2">
          <Database className="h-4 w-4 text-slate-500" />
          <h4 className="font-heading text-sm font-semibold text-slate-800">Estado por catálogo</h4>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {CATALOGOS.map((c) => {
            const st = estado[c.key] || { total: 0, ultima_carga: null };
            const ok = st.total > 0;
            return (
              <div key={c.key} data-testid={`catalogos-rips-estado-${c.key}`}
                   className={`rounded-md border p-3 text-sm ${ok ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200 bg-slate-50"}`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-slate-800">{c.label}</span>
                  <Badge variant="outline" className={ok ? "border-emerald-300 text-emerald-800" : "border-slate-300 text-slate-500"}>
                    {st.total.toLocaleString("es-MX")} / {c.esperado.toLocaleString("es-MX")}
                  </Badge>
                </div>
                <div className="mt-1 text-[11px] text-slate-500">
                  Última carga: {fmtFecha(st.ultima_carga)}
                </div>
                <div className="mt-1 font-mono text-[10px] text-slate-400">{c.key}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Búsqueda */}
      <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-3 flex items-center gap-2">
          <Search className="h-4 w-4 text-slate-500" />
          <h4 className="font-heading text-sm font-semibold text-slate-800">Consulta rápida</h4>
        </div>
        <div className="flex flex-col gap-2 md:flex-row">
          <Select value={busquedaCat} onValueChange={setBusquedaCat}>
            <SelectTrigger data-testid="catalogos-rips-search-catalogo" className="w-full md:w-64">
              <SelectValue placeholder="Selecciona un catálogo" />
            </SelectTrigger>
            <SelectContent>
              {CATALOGOS.map((c) => (
                <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input data-testid="catalogos-rips-search-input" placeholder="Buscar por clave o descripción…"
                 value={busquedaTexto} onChange={(e) => setBusquedaTexto(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && handleBuscar()} className="flex-1" />
          <Button data-testid="catalogos-rips-search-btn" onClick={handleBuscar} disabled={buscando}>
            {buscando ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            <span className="ml-1">Buscar</span>
          </Button>
        </div>
        <div className="mt-3 max-h-80 overflow-y-auto rounded border border-slate-100">
          {resultados.length === 0 ? (
            <div className="p-4 text-center text-xs text-slate-400"
                 data-testid="catalogos-rips-search-empty">
              Selecciona un catálogo, escribe una palabra clave y presiona buscar.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left">
                <tr>
                  <th className="p-2 text-xs font-medium text-slate-600">Clave</th>
                  <th className="p-2 text-xs font-medium text-slate-600">Descripción</th>
                </tr>
              </thead>
              <tbody>
                {resultados.map((r) => (
                  <tr key={`${r.clave}-${r.descripcion}`} className="border-t border-slate-100"
                      data-testid={`catalogos-rips-result-${r.clave}`}>
                    <td className="p-2 font-mono text-xs text-slate-700">{r.clave}</td>
                    <td className="p-2 text-xs text-slate-600">{r.descripcion}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {resultados.length >= 50 && (
            <div className="border-t border-slate-100 bg-amber-50 p-2 text-center text-[11px] text-amber-800">
              Mostrando primeros 50 resultados. Refina la búsqueda para acotar.
            </div>
          )}
        </div>
      </div>

      {/* Equivalencias texto legible → clave oficial ACTIVIDAD_ECONOMICA */}
      <EquivalenciasActividadPanel />
    </div>
  );
}
